import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { 
  LayoutDashboard, Users, Building, CreditCard, TrendingUp, 
  Star, Check, X, Loader2, Search, ChevronLeft, ChevronRight,
  DollarSign, Calendar, BadgeCheck, Sparkles, Image, MessageSquare, Ticket, Award, BarChart3,
  Trash2, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import AdminHeroImages from './AdminHeroImages';
import AdminTrialCodes from './AdminTrialCodes';
import AdminBadges from './AdminBadges';
import AdminAnalytics from './AdminAnalytics';
import AdminEventsPage from './AdminEventsPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar
const AdminSidebar = () => {
  const location = useLocation();
  
  const navItems = [
    { path: '/admin', label: 'Overview', icon: LayoutDashboard },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/events', label: 'Events', icon: Calendar },
    { path: '/admin/sidequests', label: 'Sidequests', icon: Sparkles },
    { path: '/admin/users', label: 'Users', icon: Users },
    { path: '/admin/transactions', label: 'Transactions', icon: CreditCard },
    { path: '/admin/referrals', label: 'Referrals', icon: TrendingUp },
    { path: '/admin/trial-codes', label: 'Trial Codes', icon: Ticket },
    { path: '/admin/badges', label: 'Badges', icon: Award },
    { path: '/admin/hero-images', label: 'Hero Images', icon: Image },
    { path: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
  ];

  return (
    <aside className="hidden lg:block w-64 bg-[#121212] border-r border-white/10 min-h-screen fixed left-0 top-16">
      <div className="p-6">
        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-4">Admin Panel</p>
        <nav className="space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all
                  ${isActive 
                    ? 'bg-red-500/10 text-red-400' 
                    : 'text-neutral-400 hover:text-white hover:bg-white/5'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

// Overview
const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch statss:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-red-400' },
    { label: 'Total Businesses', value: stats?.total_businesses || 0, icon: Building, color: 'text-purple-400' },
    { label: 'Active Subscriptions', value: stats?.active_subscriptions || 0, icon: BadgeCheck, color: 'text-green-400' },
    { label: 'Trial Subscriptions', value: stats?.trial_subscriptions || 0, icon: Ticket, color: 'text-blue-400' },
    { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: Calendar, color: 'text-amber-400' },
    { label: 'Pending Bookings', value: stats?.pending_bookings || 0, icon: Clock, color: 'text-yellow-400' },
    { label: 'Total Reviews', value: stats?.total_reviews || 0, icon: Star, color: 'text-pink-400' },
    { label: 'Active Referrals', value: stats?.active_referrals || 0, icon: TrendingUp, color: 'text-cyan-400' },
    { label: 'Unused Trial Codes', value: stats?.unused_trial_codes || 0, icon: Ticket, color: 'text-orange-400' },
    { label: 'Total Revenue', value: `$${stats?.total_revenue?.toFixed(2) || '0.00'}`, icon: DollarSign, color: 'text-emerald-400' },
  ];

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold text-white">Admin Overview</h1>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className="bg-[#121212] rounded-2xl border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-3">
                <Icon className={`w-5 h-5 ${stat.color}`} />
                <p className="text-sm text-neutral-400">{stat.label}</p>
              </div>
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-br from-red-600/20 to-purple-600/20 rounded-2xl border border-red-500/30 p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Referral Payouts Due</h3>
        <p className="text-3xl font-bold text-red-400 font-mono">
          ${stats?.referral_payouts?.toFixed(2) || '0.00'}
        </p>
        <p className="text-sm text-neutral-400 mt-2">
          Based on {stats?.active_referrals || 0} active referrals at $20/month each
        </p>
      </div>
    </div>
  );
};

// Placeholder for Clock icon used in overview
const Clock = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

// Users Management
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter && roleFilter !== 'all') params.append('role', roleFilter);
      params.append('page', page.toString());
      params.append('limit', '20');
      
      const response = await axios.get(`${API}/admin/users?${params.toString()}`);
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
    setLoadingPreview(true);
    setDeletePreview(null);
    
    try {
      const response = await axios.get(`${API}/admin/users/${user.id}/delete-preview`);
      setDeletePreview(response.data);
    } catch (error) {
      console.error('Failed to get delete preview:', error);
      toast.error(error.response?.data?.detail || 'Failed to get deletion preview');
      setDeleteModalOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/users/${userToDelete.id}`);
      toast.success(`User deleted successfully. Removed ${response.data.deleted_businesses} businesses and ${response.data.deleted_sidequests} sidequests.`);
      setDeleteModalOpen(false);
      setUserToDelete(null);
      setDeletePreview(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const roleColors = {
    admin: 'bg-red-500/20 text-red-400 border-red-500/30',
    business: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    customer: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <h1 className="text-3xl font-bold text-white">Users</h1>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="customer">Customers</SelectItem>
            <SelectItem value="business">Businesses</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>
      ) : (
        <>
          <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Name</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Email</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Role</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Joined</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-4 px-6 text-white">{user.name}</td>
                      <td className="py-4 px-6 text-neutral-400">{user.email}</td>
                      <td className="py-4 px-6">
                        <Badge className={roleColors[user.role]}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 text-neutral-400">
                        {format(parseISO(user.created_at), 'MMM d, yyyy')}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(user)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button 
                variant="outline" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="border-white/20 text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-neutral-400 px-4">Page {page} of {totalPages}</span>
              <Button 
                variant="outline" 
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
                className="border-white/20 text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Delete User</h2>
            </div>
            
            {loadingPreview ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-red-500" />
              </div>
            ) : deletePreview ? (
              <>
                <p className="text-neutral-400 mb-4">
                  Are you sure you want to delete <span className="text-white font-medium">{deletePreview.user.name}</span> ({deletePreview.user.email})?
                </p>
                
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
                  <p className="text-sm text-red-400 font-medium mb-3">This action will permanently delete:</p>
                  <ul className="space-y-2 text-sm">
                    {deletePreview.data_counts.businesses > 0 && (
                      <li className="flex items-center gap-2 text-white">
                        <Building className="w-4 h-4 text-red-400" />
                        <span><strong>{deletePreview.data_counts.businesses}</strong> business{deletePreview.data_counts.businesses !== 1 ? 'es' : ''}</span>
                      </li>
                    )}
                    {deletePreview.data_counts.sidequests > 0 && (
                      <li className="flex items-center gap-2 text-white">
                        <Sparkles className="w-4 h-4 text-red-400" />
                        <span><strong>{deletePreview.data_counts.sidequests}</strong> sidequest{deletePreview.data_counts.sidequests !== 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {deletePreview.data_counts.submissions > 0 && (
                      <li className="flex items-center gap-2 text-neutral-300">
                        <span className="w-4 h-4 text-center text-red-400">•</span>
                        <span>{deletePreview.data_counts.submissions} submission{deletePreview.data_counts.submissions !== 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {deletePreview.data_counts.bookings > 0 && (
                      <li className="flex items-center gap-2 text-neutral-300">
                        <span className="w-4 h-4 text-center text-red-400">•</span>
                        <span>{deletePreview.data_counts.bookings} booking{deletePreview.data_counts.bookings !== 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {deletePreview.data_counts.reviews > 0 && (
                      <li className="flex items-center gap-2 text-neutral-300">
                        <span className="w-4 h-4 text-center text-red-400">•</span>
                        <span>{deletePreview.data_counts.reviews} review{deletePreview.data_counts.reviews !== 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {deletePreview.data_counts.likes > 0 && (
                      <li className="flex items-center gap-2 text-neutral-300">
                        <span className="w-4 h-4 text-center text-red-400">•</span>
                        <span>{deletePreview.data_counts.likes} like{deletePreview.data_counts.likes !== 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {deletePreview.data_counts.businesses === 0 && deletePreview.data_counts.sidequests === 0 && 
                     deletePreview.data_counts.submissions === 0 && deletePreview.data_counts.bookings === 0 &&
                     deletePreview.data_counts.reviews === 0 && deletePreview.data_counts.likes === 0 && (
                      <li className="text-neutral-400">No associated data found</li>
                    )}
                  </ul>
                </div>

                <p className="text-sm text-neutral-500 mb-6">
                  This action cannot be undone. All associated files will also be deleted.
                </p>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDeleteModalOpen(false);
                      setUserToDelete(null);
                      setDeletePreview(null);
                    }}
                    disabled={deleting}
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete User
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

// Businesses Management
const AdminBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchBusinesses();
  }, [page]);

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/businesses/all?page=${page}&limit=20`);
      setBusinesses(response.data.businesses);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async (businessId, featured) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/feature?featured=${featured}`);
      toast.success(featured ? 'Business featured' : 'Business unfeatured');
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to update business');
    }
  };

  const toggleVerify = async (businessId, verified) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/verify?verified=${verified}`);
      toast.success(verified ? 'Business verified' : 'Verification removed');
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to update business');
    }
  };

  const activateSubscription = async (businessId) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/activate`);
      toast.success('Subscription activated for 30 days');
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to activate subscription');
    }
  };

  const deleteBusiness = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}`);
      toast.success('Business deleted');
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Businesses</h1>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>
      ) : (
        <>
          <div className="space-y-4">
            {businesses.map(business => (
              <div key={business.id} className="bg-[#121212] rounded-2xl border border-white/10 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Link 
                        to={`/businesses/${business.id}`}
                        className="text-lg font-semibold text-white hover:text-red-400"
                      >
                        {business.business_name}
                      </Link>
                      {business.is_featured && (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                          Featured
                        </Badge>
                      )}
                      {business.is_verified && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          Verified
                        </Badge>
                      )}
                      <Badge className={business.subscription_status === 'active' 
                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                      }>
                        {business.subscription_status}
                      </Badge>
                    </div>
                    <p className="text-sm text-neutral-400">{business.category} • {business.location}</p>
                    <div className="flex items-center gap-1 mt-2">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-neutral-300">{business.rating_avg?.toFixed(1) || '0.0'}</span>
                      <span className="text-neutral-500">({business.rating_count} reviews)</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => toggleFeature(business.id, !business.is_featured)}
                      className={`border-white/20 ${business.is_featured ? 'text-red-400' : 'text-neutral-400'}`}
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      {business.is_featured ? 'Unfeature' : 'Feature'}
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => toggleVerify(business.id, !business.is_verified)}
                      className={`border-white/20 ${business.is_verified ? 'text-green-400' : 'text-neutral-400'}`}
                    >
                      <BadgeCheck className="w-4 h-4 mr-1" />
                      {business.is_verified ? 'Unverify' : 'Verify'}
                    </Button>
                    {business.subscription_status !== 'active' && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => activateSubscription(business.id)}
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                      >
                        <CreditCard className="w-4 h-4 mr-1" />
                        Activate Sub
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => deleteBusiness(business.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button 
                variant="outline" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="border-white/20 text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-neutral-400 px-4">Page {page} of {totalPages}</span>
              <Button 
                variant="outline" 
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
                className="border-white/20 text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Transactions
const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchTransactions();
  }, [page]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/transactions?page=${page}&limit=20`);
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    paid: 'bg-green-500/20 text-green-400 border-green-500/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Transactions</h1>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-12">
          <CreditCard className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-neutral-400">No transactions yet</p>
        </div>
      ) : (
        <>
          <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">ID</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Type</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Amount</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(tx => (
                    <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-4 px-6 text-white font-mono text-sm">{tx.id.slice(0, 8)}...</td>
                      <td className="py-4 px-6 text-neutral-300 capitalize">{tx.type}</td>
                      <td className="py-4 px-6 text-green-400 font-mono">${tx.amount?.toFixed(2)}</td>
                      <td className="py-4 px-6">
                        <Badge className={statusColors[tx.payment_status]}>
                          {tx.payment_status}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 text-neutral-400">
                        {format(parseISO(tx.created_at), 'MMM d, yyyy HH:mm')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button 
                variant="outline" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="border-white/20 text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-neutral-400 px-4">Page {page} of {totalPages}</span>
              <Button 
                variant="outline" 
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
                className="border-white/20 text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Referrals
const AdminReferrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/referrals`);
      setReferrals(response.data.referrals || []);
    } catch (error) {
      console.error('Failed to fetch referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Referrals</h1>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>
      ) : referrals.length === 0 ? (
        <div className="text-center py-12">
          <TrendingUp className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-neutral-400">No referrals yet</p>
        </div>
      ) : (
        <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Referrer ID</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Referred ID</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Status</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-neutral-400">Created</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map(ref => (
                  <tr key={ref.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-4 px-6 text-white font-mono text-sm">{ref.referrer_id.slice(0, 8)}...</td>
                    <td className="py-4 px-6 text-white font-mono text-sm">{ref.referred_id.slice(0, 8)}...</td>
                    <td className="py-4 px-6">
                      <Badge className={statusColors[ref.status]}>
                        {ref.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-6 text-neutral-400">
                      {format(parseISO(ref.created_at), 'MMM d, yyyy')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <AdminSidebar />
      
      <div className="lg:ml-64 px-4 sm:px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Mobile Nav */}
          <div className="lg:hidden flex gap-1 p-1 bg-[#121212] rounded-xl mb-6 overflow-x-auto">
            {[
              { path: '/admin', label: 'Overview', icon: LayoutDashboard },
              { path: '/admin/events', label: 'Events', icon: Calendar },
              { path: '/admin/sidequests', label: 'Quests', icon: Sparkles },
              { path: '/admin/users', label: 'Users', icon: Users },
              { path: '/admin/hero-images', label: 'Hero', icon: Image },
            ].map(item => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-neutral-400 hover:text-white whitespace-nowrap"
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>

          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="events" element={<AdminEventsPage />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="businesses" element={<AdminBusinesses />} />
            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="trial-codes" element={<AdminTrialCodes />} />
            <Route path="badges" element={<AdminBadges />} />
            <Route path="hero-images" element={<AdminHeroImages />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
